/**
 * App shell — owns active-chat state, left drawer open/close,
 * right activity panel open/close, top-right Settings drawer.
 *
 * 2026-05-21 P1: mocks removed. When not paired we mount PairWall on
 * top of the shell; once `atmosStore.linkState === "paired"` it tears
 * itself down and live data flows.
 */

import { ChatView } from "./components/ChatView";
import { Drawer } from "./components/Drawer";
import { Settings } from "./components/Settings";
import { AgentWizard } from "./components/AgentWizard";
import { CommandPalette } from "./components/CommandPalette";
import { PairWall } from "./components/PairWall";
import { atmosStore } from "./lib/atmosStore";
import { atmosWS, type AgentCreateCfg } from "./lib/atmosWS";

function pickInitialChatId(): string {
  const live = atmosStore.convs();
  if (live.length > 0) return String(live[0]!.cid);
  // No live convs yet — start empty. The PairWall overlays the shell
  // until the Mac sends conv.list.r, at which point onConvs fires and
  // we switch into the first live conv.
  return "";
}

export interface AppOpts {
  insideTelegram: boolean;
  userFirstName?: string;
}

export function App(opts: AppOpts = { insideTelegram: false }): HTMLElement {
  const root = document.createElement("div");
  root.className = "app";

  let activeId = pickInitialChatId();
  let drawerOpen = false;
  let settingsOpen = false;

  const backdrop = document.createElement("div");
  backdrop.className = "drawer-backdrop";

  const drawerEl = Drawer({
    onPick: (id: string) => {
      activeId = id;
      closeDrawer();
      rerenderMain();
    },
    onCreate: (kind: "chat" | "agent") => {
      closeDrawer();
      if (kind === "chat") {
        activeId = "new";
        rerenderMain();
      } else {
        openWizard();
      }
    },
    onAgentConfigure: (a) => {
      closeDrawer();
      openWizard({
        name: a.name,
        role: a.tagline,
        prompt: "",
        model: a.model ?? "sonnet",
        tools: a.tools ?? ["code"],
      });
    },
    getActiveId: () => activeId,
  });

  function openWizard(initial?: AgentCreateCfg): void {
    const w = AgentWizard({
      initial,
      onCancel: () => w.remove(),
      onCreate: (cfg: AgentCreateCfg) => {
        atmosWS.createAgent(cfg);
        w.remove();
      },
    });
    root.appendChild(w);
  }

  // When Mac broadcasts conv list, switch the main view to the first
  // conv if we're still on the placeholder empty state.
  atmosStore.onConvs(() => {
    if (activeId === "" || activeId === "new") {
      const convs = atmosStore.convs();
      if (convs.length > 0) {
        const latest = convs[convs.length - 1]!;
        activeId = String(latest.cid);
        rerenderMain();
      }
    }
  });

  let settingsEl: HTMLElement | null = null;

  const main = document.createElement("main");
  main.className = "app__main";

  function rerenderMain(): void {
    main.replaceChildren(
      ChatView(activeId, openDrawer, openAgents, openSettings, openCmd),
    );
  }

  function openCmd(prefill: (text: string) => void): void {
    const palette = CommandPalette({
      onCancel: () => palette.remove(),
      onPick: (cmdLine, send) => {
        palette.remove();
        if (send) {
          const cid = Number(activeId);
          if (Number.isFinite(cid)) atmosWS.sendMsg(cid, cmdLine);
          else atmosWS.sendNew(cmdLine);
        } else {
          prefill(cmdLine + " ");
        }
      },
    });
    root.appendChild(palette);
  }

  function syncBackdrop(): void {
    backdrop.classList.toggle("drawer-backdrop--open", drawerOpen || settingsOpen);
  }

  function openDrawer(): void {
    if (settingsOpen) closeSettings();
    drawerOpen = true;
    drawerEl.classList.add("drawer--open");
    syncBackdrop();
  }

  function closeDrawer(): void {
    drawerOpen = false;
    drawerEl.classList.remove("drawer--open");
    syncBackdrop();
  }

  function openAgents(): void {
    openDrawer();
    (drawerEl as unknown as { setTab?: (t: "chats" | "agents") => void })
      .setTab?.("agents");
  }

  function openSettings(): void {
    if (drawerOpen) closeDrawer();
    if (!settingsEl) {
      settingsEl = Settings(() => closeSettings());
      root.append(settingsEl);
    }
    settingsOpen = true;
    settingsEl.classList.add("settings--open");
    syncBackdrop();
  }

  function closeSettings(): void {
    settingsOpen = false;
    settingsEl?.classList.remove("settings--open");
    syncBackdrop();
  }

  backdrop.addEventListener("click", () => {
    if (drawerOpen) closeDrawer();
    if (settingsOpen) closeSettings();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (drawerOpen) closeDrawer();
    if (settingsOpen) closeSettings();
  });

  rerenderMain();
  root.append(main, backdrop, drawerEl);

  // Pair gate — only paired sessions get the live shell. Defer the
  // mount one microtask so an auto-connect that finishes synchronously
  // (cached config, fast LAN) flips linkState before we decide whether
  // to show the wall. The wall self-removes on pair via onLink.
  let wall: HTMLElement | null = null;
  function syncPairGate(): void {
    if (atmosStore.linkState() === "paired") {
      if (wall) { wall.remove(); wall = null; }
      return;
    }
    if (!wall) {
      wall = PairWall({
        insideTelegram: opts.insideTelegram,
        userFirstName: opts.userFirstName,
      });
      root.appendChild(wall);
    }
  }
  queueMicrotask(syncPairGate);
  atmosStore.onLink(syncPairGate);

  return root;
}

export default App;
